import { useState } from "react";
import { Download, Expand, LoaderCircle } from "lucide-react";
import { useEventContent } from "@/context/EventContentContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const spans = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-2",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-2",
];

function imageExtension(type = "") {
  const extensions = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
  };

  return extensions[type] || "jpg";
}

export default function Gallery() {
  const { gallery } = useEventContent();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [downloadingIndex, setDownloadingIndex] = useState(null);

  const downloadImage = async (src, index) => {
    setDownloadingIndex(index);

    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error("No se pudo descargar la imagen");

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `cuchara-grande-${index + 1}.${imageExtension(blob.type)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      const link = document.createElement("a");
      link.href = src;
      link.download = `cuchara-grande-${index + 1}.jpg`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    } finally {
      setDownloadingIndex(null);
    }
  };

  const selectedImage = selectedIndex === null ? null : gallery[selectedIndex];

  return (
    <div data-testid="gallery-page" className="section-pad">
      <div className="container-cg">
        <div className="overline text-[var(--brand-primary)]">Galería</div>
        <h1 className="font-serif-cg text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
          Atmósfera, mesa y memoria.
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4 mt-12">
          {gallery.map((src, idx) => (
            <figure
              key={src + idx}
              data-testid={`gallery-item-${idx}`}
              className={`relative overflow-hidden card-cg img-zoom-host group ${spans[idx % spans.length]}`}
            >
              <button
                type="button"
                className="relative block h-full w-full text-left"
                onClick={() => setSelectedIndex(idx)}
                aria-label={`Abrir fotografía ${idx + 1}`}
              >
                <img
                  src={src}
                  alt={`Cuchara Grande ${idx + 1}`}
                  className="img-zoom w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute inset-0 flex items-end bg-black/20 p-3 text-white opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                  <span className="inline-flex items-center gap-2 bg-black/65 px-3 py-2 text-xs font-semibold uppercase tracking-wider">
                    <Expand size={15} /> Abrir
                  </span>
                </span>
              </button>

              <button
                type="button"
                className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center bg-white/95 text-[var(--brand-primary)] shadow-md transition-colors hover:bg-white disabled:opacity-60"
                onClick={() => downloadImage(src, idx)}
                disabled={downloadingIndex === idx}
                aria-label={`Descargar fotografía ${idx + 1}`}
                title="Descargar fotografía"
              >
                {downloadingIndex === idx ? <LoaderCircle className="animate-spin" size={18} /> : <Download size={18} />}
              </button>
            </figure>
          ))}
        </div>
      </div>

      <Dialog open={selectedIndex !== null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
        <DialogContent className="w-[95vw] max-w-6xl border-0 bg-[#111]/95 p-3 text-white shadow-2xl sm:rounded-none">
          <DialogTitle className="sr-only">
            Fotografía {selectedIndex === null ? "" : selectedIndex + 1} de Cuchara Grande
          </DialogTitle>

          {selectedImage ? (
            <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3">
              <img
                src={selectedImage}
                alt={`Cuchara Grande ${selectedIndex + 1}`}
                className="max-h-[78vh] max-w-full object-contain"
              />
              <button
                type="button"
                className="inline-flex items-center gap-2 bg-white px-4 py-2.5 text-sm font-semibold text-[#1A2111] hover:bg-[#F0F0E8] disabled:opacity-60"
                onClick={() => downloadImage(selectedImage, selectedIndex)}
                disabled={downloadingIndex === selectedIndex}
              >
                {downloadingIndex === selectedIndex ? (
                  <LoaderCircle className="animate-spin" size={17} />
                ) : (
                  <Download size={17} />
                )}
                Descargar fotografía
              </button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
